// Survey-related type definitions
export interface Contact {
  contactId: string;
  name: string;
  title?: string;
  licenseNo?: string;
  email: string;
  phoneNumber?: string;
  order: number;
}

export interface SurveySection {
  surveySectionId: string;
  surveyId: string;
  name: string;
  /** Author-defined label; falls back to the default label for `name` when unset. */
  displayName?: string | null;
  isCustom: boolean;
  isHidden: boolean;
  order: number;
  isDeletable: boolean;
  isRenamable: boolean;
  isReorderable: boolean;
  hasPDF: boolean;
  pdfs: unknown[];
}

export interface Survey {
  id: string;
  name: string;
  propertyCount?: number;
  description?: string;
  coverBackgroundImage?: string | { cloudfrontUrl: string };
  createdAt?: string;
  updatedAt?: string;
  updatedOn?: string;
  marketMapStyle?: string;
  propertyMapStyle?: string;
  projectId?: string;
  pdfPaperSize?: "Portrait" | "Landscape";
  isDisplayClientLogo?: boolean;
  isSurveyShortlistEnabled?: boolean;
  isClientCommentEnabled?: boolean;
  isClientPdfDownloadEnabled?: boolean;
  marketMapBamData: string;
  isIncludeFlyers?: boolean;
  isIncludePhotos?: boolean;
  isIncludeSitePlans?: boolean;
  isIncludeFlyersPC?: boolean;
  isIncludeSitePlansPC?: boolean;
  sitePlanLabel?: string;
  flyerLabel?: string;
  isMarinePorts?: boolean;
  // Cover page fields
  coverTitle?: string;
  coverSubTitle?: string;
  coverDate?: string;
  isDisplayClientName?: boolean;
  clientName?: string;
  // Client logo styling options
  isClientLogoTransparent?: boolean;
  unrestrictedLogoSize?: boolean;
  contacts?: Contact[];
  properties?: Property[];
  sections: SurveySection[];
  surveyPropertyFieldRule: SurveyPropertyFieldRule;
  isIncludeHeader?: boolean;
  isIncludeLegend?: boolean;
  isIncludeMapKey?: boolean;
  isIncludeLeaseComps?: boolean;
  isIncludePolygons?: boolean;
  showCurrentLocation?: boolean;
  showCurrentLocationPD: boolean;
  showPropertyPhoto?: boolean;
  isIncludeTourSch?: boolean;
  customLegends?: {
    displayFields: {
      hasPropertyName: boolean;
      hasAddressLine1: boolean;
      hasCity: boolean;
      hasState: boolean;
      hasZipCode: boolean;
    };
  };
  // Tour schedule display flags
  isIncludeTourPropertyName?: boolean;
  isIncludeTourStreetAddress?: boolean;
  isIncludeTourCity?: boolean;
  isIncludeTourState?: boolean;
  isIncludeTourZipCode?: boolean;
}

export type SurveyPropertyFieldRule = {
  surveyId: string;
  propertyFieldRuleList: PropertyFieldRule[];
  status?: string;
  fieldKey?: string;
  fieldLabel?: string;
  order?: number;
};

export type PropertyFieldRule = {
  fieldId?: string;
  fieldKey: string;
  fieldLabel: string;
  isHidden: boolean;
  order: number;
};

export interface Property {
  id: string;
  propertyId?: string;
  name?: string;
  propertyName?: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  address1: string;
  address2?: string;
  latitude: number | string;
  longitude: number | string;
  availableSF?: string;
  thumbnailUrl?: string;
  isHidden: boolean;
  isClientShortlist?: boolean;
  isCurrentLocation?: boolean;
  isIncludeTourSchedule?: boolean;
  marketValue?: number;
  propertyType: string;
  description?: string;
  squareFootage?: number;
  yearBuilt?: number;
  amenities?: string[];
  bamMapData?: string;
  order?: number;
  customFields?: PropertyCustomField[];
  stopDetails?: string;
  customStopDetails?: CustomStopDetail[];
  linkedProperty?: LinkedStopProperty;
}

export interface SurveyCardProps {
  survey: Survey;
}

export interface SurveyListProps {
  surveys?: Survey[];
  isLoading?: boolean;
  error?: string | null;
}

// Survey API response types
export type SurveyHomepageResponse = Survey[];

// Survey creation/update types (for future use)
export interface CreateSurveyRequest {
  name: string;
  description?: string;
}

export interface UpdateSurveyRequest extends Partial<CreateSurveyRequest> {
  id: string;
}

export type PropertyCustomField = {
  // TODO: Id?
  customField: CustomField;
  value: string;
  updatedOn: string;
  fieldKey?: string;
};

export type CustomField = {
  fieldKey: string;
  fieldLabel?: string;
  ownerId?: string;
  ownerType?: string;
  customFieldId?: string;
  surveyId?: string;
  name?: string;
  order?: number;
  createdOn?: string;
  updatedOn?: string;
  createdBy?: string;
  updatedBy?: string;
};

/**
 * Enum for tour stop types
 * CUSTOM_LOCATION: A custom stop location added to the tour
 * CURRENT_LOCATION: The actual property location
 */
export enum StopType {
  CUSTOM_LOCATION = "CUSTOM_LOCATION",
  CURRENT_LOCATION = "CURRENT_LOCATION",
}

/**
 * Nested custom stop detail within a tour stop
 * Contains additional information about a custom location
 */
export interface CustomStopDetail {
  customStopDetailId?: string;
  name: string;
  description?: string;
  notes?: string;
}

/**
 * Linked property information for a tour stop
 * References another property associated with this stop
 */
export interface LinkedStopProperty {
  propertyId?: string;
  propertyName: string;
  address1: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
}

/**
 * Individual tour stop for a property
 * Can be either CUSTOM_LOCATION or CURRENT_LOCATION type
 */
export interface TourStop {
  stopId?: string;
  tourStopId?: string;
  surveyId?: string;
  propertyId?: string;
  property?: {
    id?: string;
    propertyId?: string;
  };
  stopType?: StopType | string;
  label?: string;
  address?: string;
  stopDetails?: string;
  order?: number;
  active?: boolean;
  customStopDetails?: CustomStopDetail[];
  linkedProperty?: LinkedStopProperty;
}

/**
 * API response structure for GET /tour-stops/{surveyId}
 * The actual API response has 'stops' as the primary field
 */
export interface TourStopsResponse {
  stops: TourStop[]; // Primary API field
  tourStops?: TourStop[]; // Legacy/alternative field
  data?: TourStop[]; // Alternative field
}

// TypeScript interfaces for Marine Ports GeoJSON response
export interface PortProperties {
  OBJECTID: number;
  ID: number;
  PORT_NAME: string;
  IMPORTS: number;
  EXPORTS: number;
  DOMESTIC: number;
  SE_ANNO_CAD_DATA: string;
  PORT: number;
  TYPE: string;
  LATITUDE1: number;
  LONGITUDE1: number;
  RANK: number;
  TOTAL: number;
  FOREIGN: number;
}

export interface PortGeometry {
  type: "Point";
  coordinates: [number, number]; // [longitude, latitude]
}

export interface PortFeature {
  type: "Feature";
  properties: PortProperties;
  geometry: PortGeometry;
}

export interface MarinePortsResponse {
  type: "FeatureCollection";
  name: string;
  features: PortFeature[];
}
